// One notice band per page (product convention this shell establishes for every later task):
// a single place for the page's error, success or informational message. DESIGN-VISUAL.md §3.2
// gives `--color-attention` "a 3 px left edge on a notice band" — the one literal px value this
// file uses is that sentence, not an invented one. Icons accompany the word, they never replace it
// (DESIGN-VISUAL.md §8, §10.16): the tone is carried by the heading text and the icon is
// `aria-hidden`.
import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import type { ReactNode } from 'react';

export type NoticeTone = 'info' | 'settled' | 'attention';

const TONE_ICON = { info: Info, settled: CheckCircle2, attention: AlertTriangle } as const;
const TONE_TEXT_CLASS: Record<NoticeTone, string> = {
  info: 'text-ink',
  settled: 'text-settled',
  attention: 'text-attention',
};
const TONE_BORDER_CLASS: Record<NoticeTone, string> = {
  info: 'border-l-ink',
  settled: 'border-l-settled',
  attention: 'border-l-attention',
};

export interface NoticeBandProps {
  tone?: NoticeTone;
  title?: string;
  children: ReactNode;
}

export function NoticeBand({ tone = 'info', title, children }: NoticeBandProps) {
  const Icon = TONE_ICON[tone];
  return (
    <div
      role={tone === 'attention' ? 'alert' : 'status'}
      className={[
        'flex gap-s3 rounded-panel border border-rule bg-paper p-s4',
        'border-l-[3px]',
        TONE_BORDER_CLASS[tone],
      ].join(' ')}
    >
      <Icon aria-hidden="true" className={`h-5 w-5 flex-shrink-0 ${TONE_TEXT_CLASS[tone]}`} strokeWidth={1.5} />
      <div className="flex flex-col gap-s1">
        {title ? <p className={`text-body font-label ${TONE_TEXT_CLASS[tone]}`}>{title}</p> : null}
        <div className="text-body text-ink">{children}</div>
      </div>
    </div>
  );
}
